import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { AlertTriangle, MessageSquarePlus } from "lucide-react";
import type { ReviewRow, RiskLevel } from "@/types";

interface NewDiscussionDialogProps {
  onCreateDiscussion: (title: string, content: string) => Promise<boolean>;
  trigger: React.ReactNode;
  reviewTable?: ReviewRow[] | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const riskLevelColors: Record<RiskLevel, string> = {
  "acceptable": "text-green-400 bg-green-400/10",
  "acceptable-clarify": "text-yellow-400 bg-yellow-400/10",
  "not-acceptable": "text-red-400 bg-red-400/10",
};

const riskLevelLabels: Record<RiskLevel, string> = {
  "acceptable": "Acceptable",
  "acceptable-clarify": "Clarify",
  "not-acceptable": "Not Acceptable",
};

export function NewDiscussionDialog({
  onCreateDiscussion,
  trigger,
  reviewTable,
  open: openProp,
  onOpenChange,
}: NewDiscussionDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = onOpenChange !== undefined;
  const open = isControlled ? (openProp ?? false) : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const [mode, setMode] = useState<"custom" | "risk">("custom");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) return;

    setIsCreating(true);
    const success = await onCreateDiscussion(title.trim(), content.trim());
    setIsCreating(false);

    if (success) {
      setOpen(false);
      setTitle("");
      setContent("");
      setMode("custom");
    }
  };

  const handleRiskSelect = (row: ReviewRow) => {
    setTitle(`Risk: ${row.section}`);
    setContent(`I'd like to discuss the risk in section "${row.section}":\n\n**Risk Level:** ${riskLevelLabels[row.riskLevel]}\n**Reasoning:** ${row.riskReasoning}\n\nMy question:`);
    setMode("custom");
  };

  const hasRisks = reviewTable && reviewTable.length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-[var(--lm-foreground-strong)] dark:text-white">
            Start New Discussion
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          {hasRisks && (
            <div className="flex gap-2 rounded-lg bg-[var(--tab-segment-bg)] p-1 dark:bg-slate-800">
              <button
                onClick={() => setMode("custom")}
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  mode === "custom"
                    ? "bg-[var(--tab-segment-active-bg)] text-[var(--lm-foreground-strong)] shadow-sm dark:bg-slate-700 dark:text-white"
                    : "text-[var(--lm-muted)] hover:text-[var(--lm-foreground-strong)] dark:text-slate-400 dark:hover:text-slate-200"
                )}
              >
                <MessageSquarePlus className="mr-1.5 inline h-4 w-4" />
                Custom Topic
              </button>
              <button
                onClick={() => setMode("risk")}
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  mode === "risk"
                    ? "bg-[var(--tab-segment-active-bg)] text-[var(--lm-foreground-strong)] shadow-sm dark:bg-slate-700 dark:text-white"
                    : "text-[var(--lm-muted)] hover:text-[var(--lm-foreground-strong)] dark:text-slate-400 dark:hover:text-slate-200"
                )}
              >
                <AlertTriangle className="mr-1.5 inline h-4 w-4" />
                Discuss a Risk
              </button>
            </div>
          )}

          {mode === "risk" && hasRisks ? (
            <div className="min-w-0">
              <label className="mb-2 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-300">
                Select a risk to discuss
              </label>
              <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-2 dark:border-slate-700 dark:bg-slate-800">
                {reviewTable.map((row, index) => (
                  <button
                    key={index}
                    onClick={() => handleRiskSelect(row)}
                    className="w-full rounded-lg border border-[var(--lm-border-subtle)] bg-white p-3 text-left transition-all hover:border-violet-500/40 hover:bg-[var(--lm-hover)] dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-violet-500 dark:hover:bg-slate-700"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="line-clamp-2 min-w-0 flex-1 break-words font-medium text-[var(--lm-foreground-strong)] dark:text-white">
                          {row.section}
                        </div>
                        <span
                          className={cn(
                            "flex-shrink-0 rounded px-2 py-0.5 text-xs font-medium",
                            riskLevelColors[row.riskLevel]
                          )}
                        >
                          {riskLevelLabels[row.riskLevel]}
                        </span>
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs text-[var(--lm-muted)] dark:text-slate-400">
                        {row.riskReasoning}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-300">
                  Title
                </label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What's this discussion about?"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-300">
                  First Message
                </label>
                <div className="rounded-lg border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-3 dark:border-slate-600 dark:bg-slate-800">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write the first message..."
                    rows={4}
                    className="w-full resize-none bg-transparent text-sm text-[var(--lm-foreground-strong)] placeholder:text-[var(--lm-muted)] focus:outline-none dark:text-white dark:placeholder:text-slate-400"
                  />
                  <p className="mt-2 text-xs text-[var(--lm-muted)] dark:text-slate-500">
                    Type <span className="text-[var(--lm-foreground-strong)] dark:text-slate-400">@</span> to mention organization members
                  </p>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setMode("custom");
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            {mode === "custom" && (
              <Button
                onClick={handleCreate}
                disabled={!title.trim() || !content.trim() || isCreating}
              >
                {isCreating ? "Creating..." : "Create Discussion"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
